/**
 * menuFonts.ts — Pure font scaling and contrast style helpers for menus.
 *
 * Provides injectable dependency for AccessibilitySystem so consumers can
 * scale fonts and resolve UI contrast styles without importing singletons.
 */

import { AccessibilitySystem, AccessibilitySettings } from "@babylon/systems/AccessibilitySystem";

/**
 * Scale a base font size by the accessibility system's fontScale setting.
 * Result is rounded to the nearest integer.
 *
 * @param base — Base font size in pixels
 * @param a11y — Accessibility system (injectable for testing); defaults to singleton
 * @returns Scaled and rounded font size
 */
export function scaledFont(
  base: number,
  a11y: { state: { fontScale: number } } = AccessibilitySystem.getInstance()
): number {
  return Math.round(base * a11y.state.fontScale);
}

/**
 * Resolve contrast style values based on high-contrast UI setting.
 * Matches the values used in HUD.ts for consistency.
 *
 * @param highContrastUI — When true, use high-contrast black panel + thicker border
 * @returns Object with background color, thickness, and shadow flag
 */
export function resolveContrastStyle(
  highContrastUI: boolean
): { background: string; thickness: number; shadow: boolean } {
  // PANEL_BG from HUD.ts line 73, replicated here with reference
  const PANEL_BG = "rgba(10, 6, 18, 0.94)";

  if (highContrastUI) {
    return {
      background: "rgba(0, 0, 0, 0.98)",
      thickness: 3,
      shadow: true,
    };
  }

  return {
    background: PANEL_BG,
    thickness: 1.5,
    shadow: false,
  };
}
